import { type FC, memo, type MouseEvent, useCallback, useContext } from "react";
import { isNil } from "lodash-es";
import { Button, ButtonKind, ButtonSize, ButtonVariant } from "@stubhub/broadway-web";
import { HeartFillIcon, HeartOutlineIcon, NotificationIcon } from "@stubhub/broadway-web/icons/functional";
import { Feature, RecordParticipationTypes } from "@viagogo/feature";
import { Content } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { drawerButton } from "src/website-apps/responsive/stubhub-performer/components/mobile-banner/styles";
import { NotificationFillIcon } from "src/website-apps/shared/components/FavoritesV2Buttons/FavoritesV2Button/icons/notification-fill";
import { FavoritesContext } from "src/website-apps/shared/components/grid/favorites-fs/FavoritesContextTyped";
import { useResolveRedirectUrl } from "src/website-apps/shared/components/grid/favorites-fs/hooks/useResolveRedirectUrl";
import type { FavoritesContextType } from "src/website-apps/shared/components/grid/favorites-fs/types";

interface Props {
  className?: string;
  entityId: number;
  entityType: number;
  stopPropagation?: boolean;
  preventDefault?: boolean;
  authRedirectUrl?: string;
  onAfterClick?: () => void;
}

/**
 * List-style favorite button for drawer/list interfaces.
 * Displays as a clickable row with icon and "Add to favorites" text.
 */
export const ListFavButton: FC<Props> = memo(
  ({ entityId, entityType, stopPropagation = false, preventDefault = false, authRedirectUrl = "", onAfterClick }) => {
    const { favoritesEnabled, favorites, handleFavoriteRemoval, handleFavorite } =
      useContext<FavoritesContextType>(FavoritesContext);

    const redirectUrl = useResolveRedirectUrl(authRedirectUrl);

    const isFavorite = favorites[entityType]?.[entityId] ?? false;

    const makeFavorite = useCallback(
      (e: MouseEvent) => {
        handleFavorite(entityType, entityId, {
          stopPropagation,
          preventDefault,
          redirectUrl,
        })(e);
        onAfterClick?.();
      },
      [entityType, entityId, stopPropagation, redirectUrl, preventDefault, handleFavorite, onAfterClick],
    );

    const removeFavorite = useCallback(
      (e: MouseEvent) => {
        handleFavoriteRemoval(entityType, entityId, {
          stopPropagation,
          preventDefault,
          redirectUrl,
        })(e);
        onAfterClick?.();
      },
      [entityType, entityId, stopPropagation, redirectUrl, preventDefault, handleFavoriteRemoval, onAfterClick],
    );

    const onClickHandler = isFavorite ? removeFavorite : makeFavorite;

    if (!favoritesEnabled) {
      return null;
    }

    if (isNil(entityId) || isNil(entityType)) {
      return null;
    }

    return (
      <Feature
        id={FeatureSetEnum.TMPerformerReplaceHeartWithNotificationIcon}
        recordType={RecordParticipationTypes.IN_VIEW}
        control={() => (
          <Button
            classes={{ root: drawerButton }}
            variant={ButtonVariant.Ghost}
            size={ButtonSize.Default}
            kind={ButtonKind.Neutral}
            startIcon={isFavorite ? HeartFillIcon : HeartOutlineIcon}
            onClick={onClickHandler}
          >
            <Content id="cid$#3928802#" />
          </Button>
        )}
        variant={() => (
          <Button
            classes={{ root: drawerButton }}
            variant={ButtonVariant.Ghost}
            size={ButtonSize.Default}
            kind={ButtonKind.Neutral}
            startIcon={isFavorite ? NotificationFillIcon : NotificationIcon}
            onClick={onClickHandler}
          >
            <Content id="cid$#3928802#" />
          </Button>
        )}
      />
    );
  },
);
ListFavButton.displayName = "ListFavButton";
